import { Input as UiInput } from "../ui/Input";
import { Checkbox as UiCheckbox } from "../ui/Checkbox";
import { LogIn, TriangleAlert, WifiOff, Wifi } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { LoginInput, TenantContext } from "../../types";
import { AppModal, Button, Chip } from "../ui";

const rememberedEmailKey = "club-pos:remembered-email";

function getRememberedEmail() {
  try {
    return window.localStorage.getItem(rememberedEmailKey) ?? "";
  } catch {
    return "";
  }
}

function saveRememberedEmail(email: string, shouldRemember: boolean) {
  try {
    if (shouldRemember) {
      window.localStorage.setItem(rememberedEmailKey, email);
    } else {
      window.localStorage.removeItem(rememberedEmailKey);
    }
  } catch {
    // El acceso debe seguir funcionando aunque el navegador bloquee el almacenamiento local.
  }
}

type LoginScreenProps = {
  allowOfflineEnter: boolean;
  cachedContext: TenantContext | null;
  conflictAccountName: string | null;
  error: string | null;
  isBusy: boolean;
  isOnline: boolean;
  onCancelLoginConflict: () => void;
  onForceLoginConflict: () => void;
  onLogin: (input: LoginInput) => Promise<void>;
  onOfflineEnter: () => void;
};

export function LoginScreen({
  allowOfflineEnter,
  cachedContext,
  conflictAccountName,
  error,
  isBusy,
  isOnline,
  onCancelLoginConflict,
  onForceLoginConflict,
  onLogin,
  onOfflineEnter,
}: LoginScreenProps) {
  const [email, setEmail] = useState(getRememberedEmail);
  const [password, setPassword] = useState("");
  const [rememberAccount, setRememberAccount] = useState(() =>
    Boolean(getRememberedEmail()),
  );
  const cancelConflictButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!conflictAccountName) return undefined;

    cancelConflictButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !isBusy) {
        onCancelLoginConflict();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [conflictAccountName, isBusy, onCancelLoginConflict]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim();
    saveRememberedEmail(normalizedEmail, rememberAccount);
    void onLogin({
      email: normalizedEmail,
      password,
    });
  }

  return (
    <main className="flex h-full overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] items-center justify-center bg-[var(--background)] p-4 text-[var(--foreground)]">
      <section className="w-full max-w-md rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--surface)] p-6 shadow-[var(--shadow)]">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-normal text-[var(--accent)]">
              Tickit
            </p>
            <h1 className="mt-2 text-3xl font-bold text-[var(--foreground)]">
              Acceso al negocio
            </h1>
          </div>
          <Chip
            icon={isOnline ? Wifi : WifiOff}
            tone={isOnline ? "success" : "danger"}
          >
            {isOnline ? "Online" : "Offline"}
          </Chip>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-sm font-semibold text-[var(--muted)]">
              Email
            </span>
            <UiInput
              className="mt-1 h-12 w-full rounded-(--radius) !border bg-(--field)! px-3 text-[var(--field-foreground)] outline-none focus:ring-2 focus:ring-[var(--accent)]"
              autoComplete="username"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="usuario@negocio.com"
              required
              type="email"
              value={email}
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-[var(--muted)]">
              Contraseña
            </span>
            <UiInput
              className="mt-1 h-12 w-full rounded-[var(--radius)] border-1 !bg-[var(--field)] px-3 text-[var(--field-foreground)] outline-none focus:ring-2 focus:ring-[var(--accent)]"
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          <UiCheckbox
            checked={rememberAccount}
            className="min-h-11 text-sm font-semibold text-[var(--foreground)]"
            onChange={setRememberAccount}
          >
            Recordar cuenta
          </UiCheckbox>
          {error ? (
            <div className="rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--danger-soft)] p-3 text-sm font-semibold text-[var(--danger)]">
              {error}
            </div>
          ) : null}

          <Button
            disabled={!isOnline || isBusy}
            fullWidth
            size="lg"
            type="submit"
            variant="primary"
          >
            <LogIn className="h-5 w-5" />
            Entrar
          </Button>
        </form>

        {allowOfflineEnter && cachedContext?.role === "cashier" ? (
          <Button
            className="mt-3"
            disabled={isBusy}
            fullWidth
            onClick={onOfflineEnter}
            type="button"
            variant="secondary"
          >
            <WifiOff className="h-5 w-5" />
            Entrar offline en {cachedContext.tenantName}
          </Button>
        ) : null}
      </section>

      {conflictAccountName ? (
        <AppModal
          containerClassName="!p-4"
          maxWidth={512}
          dismissDisabled
          label="Esta cuenta ya está conectada"
          onClose={onCancelLoginConflict}
        >
          <section
            aria-describedby="login-conflict-description"
            aria-labelledby="login-conflict-title"
            className="w-full rounded-[var(--radius)] border border-[var(--separator)] bg-[var(--surface)] p-6 text-[var(--foreground)] shadow-[var(--shadow)]"
          >
            <div className="flex items-start gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--danger-soft)] text-[var(--danger)]">
                <TriangleAlert className="h-6 w-6" />
              </span>
              <div>
                <h2 className="text-xl font-black" id="login-conflict-title">
                  Esta cuenta ya está conectada
                </h2>
                <p
                  className="mt-2 text-sm leading-6 text-[var(--muted)]"
                  id="login-conflict-description"
                >
                  La cuenta{" "}
                  <strong className="text-[var(--foreground)]">
                    {conflictAccountName}
                  </strong>{" "}
                  está abierta en otro dispositivo o pestaña. Si fuerzas la
                  conexión, la sesión anterior se cerrará y podría perder
                  cambios que aún no haya guardado.
                </p>
              </div>
            </div>

            {error ? (
              <div className="mt-4 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--danger-soft)] p-3 text-sm font-semibold text-[var(--danger)]">
                {error}
              </div>
            ) : null}

            <div className="mt-6 grid gap-3 grid-cols-2 justify-items-center">
              <Button
                disabled={isBusy}
                onClick={onCancelLoginConflict}
                ref={cancelConflictButtonRef}
                type="button"
                variant="secondary"
              >
                Cancelar login
              </Button>
              <Button
                disabled={isBusy}
                onClick={onForceLoginConflict}
                type="button"
                variant="danger"
              >
                {isBusy ? "Conectando..." : "Forzar conexión"}
              </Button>
            </div>
          </section>
        </AppModal>
      ) : null}
    </main>
  );
}
